/**
 * Rewrite model maths into the delimiters remark-math parses.
 *
 * A port of the backend's `normalize_math` (backend/src/llm/math_markdown.py),
 * same behaviour, for text the client renders before the backend has stored
 * it - a streaming chat answer. remark-math understands `$...$` inline and
 * `$$...$$` display maths, and a display block only when its `$$` sit on
 * their own lines. Three model habits break that:
 *
 * - `\(...\)` and `\[...\]` delimiters: not maths to remark-math, so the
 *   formula shows as raw LaTeX.
 * - a display block opened on the same line as its content and closed on a
 *   later line (`$$x^2+bx\n=-c$$`): read as inline maths, after which every
 *   `$` pairs with the wrong partner.
 * - a literal dollar inside maths (`$\$30{,}000$`): becomes
 *   `\text{\textdollar}` (KaTeX refuses a bare `\textdollar` in maths); an
 *   escaped dollar in prose is left as it is.
 * - money in prose ("costs $5 and $10"): a `$` before a digit whose partner
 *   follows a space or comes before a digit (pandoc's rule) is escaped.
 *
 * Code (fenced and inline) is never touched. Safe on partial text: only
 * closed pairs are rewritten, and a fence still open at the end of a
 * streaming answer counts as code to its end (the one difference from the
 * backend, which only ever sees whole answers).
 */

const CODE = /```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]+`/g;
const DISPLAY_BRACKETS = /\\\[([\s\S]+?)\\\]/g;
const INLINE_PARENS = /\\\(([\s\S]+?)\\\)/g;
const DISPLAY_DOLLARS = /\$\$([\s\S]+?)\$\$/g;
const placeholder = (i: number) => `\u0000CODE${i}\u0000`;

function block(inner: string): string {
  return `\n\n$$\n${inner.trim()}\n$$\n\n`;
}

function display(match: string, inner: string): string {
  // One-line display maths already parses; multi-line needs its own lines.
  return inner.trim().includes('\n') ? block(inner) : match;
}

/** `\$` inside `$...$` / `$$...$$` becomes `\text{\textdollar}`. */
function dollarsInMath(text: string): string {
  if (!text.includes('\\$')) return text;
  let out = '';
  let inline = false;
  let displayMode = false;
  let i = 0;
  while (i < text.length) {
    if (text.startsWith('\\$', i)) {
      out += inline || displayMode ? '\\text{\\textdollar}' : '\\$';
      i += 2;
    } else if (text.startsWith('$$', i) && !inline) {
      displayMode = !displayMode;
      out += '$$';
      i += 2;
    } else if (text[i] === '$' && !displayMode) {
      inline = !inline;
      out += '$';
      i += 1;
    } else {
      out += text[i];
      i += 1;
    }
  }
  return out;
}

/** Where the `$` closing inline maths opened before `start` is (an
 *  unescaped single `$` on the same line), or -1. */
function closing(text: string, start: number): number {
  let i = start;
  while (i < text.length && text[i] !== '\n') {
    if (text[i] === '\\') {
      i += 2;
      continue;
    }
    if (text[i] === '$') return text.startsWith('$$', i) ? -1 : i;
    i += 1;
  }
  return -1;
}

const isDigit = (c: string | undefined) =>
  c !== undefined && c >= '0' && c <= '9';

/** `$5 and $10` -> `\$5 and \$10`: money, not maths (see the header). */
function escapeMoney(text: string): string {
  if (!/\$\d/.test(text)) return text;
  let out = '';
  let displayMode = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (c === '\\') {
      out += text.slice(i, i + 2);
      i += 2;
      continue;
    }
    if (text.startsWith('$$', i)) {
      displayMode = !displayMode;
      out += '$$';
      i += 2;
      continue;
    }
    if (c === '$' && !displayMode) {
      const end = closing(text, i + 1);
      const money =
        isDigit(text[i + 1]) &&
        (end < 0 || /\s/.test(text[end - 1]) || isDigit(text[end + 1]));
      if (money) {
        out += '\\$';
        i += 1;
        continue;
      }
      if (end < 0) {
        out += '$';
        i += 1;
        continue;
      }
      // A maths span, copied whole so its closing `$` opens nothing.
      out += text.slice(i, end + 1);
      i = end + 1;
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}

/** `text` with maths in `$` / `$$` form, display blocks on their own lines. */
export function normalizeMath(text: string): string {
  if (!text.includes('\\(') && !text.includes('\\[') && !text.includes('$')) {
    return text;
  }
  const code: string[] = [];
  let out = text.replace(CODE, match => {
    code.push(match);
    return placeholder(code.length - 1);
  });
  out = out.replace(DISPLAY_BRACKETS, (_m, inner: string) => block(inner));
  out = out.replace(INLINE_PARENS, (_m, inner: string) => `$${inner.trim()}$`);
  out = out.replace(DISPLAY_DOLLARS, display);
  out = dollarsInMath(out);
  out = escapeMoney(out);
  out = out.replace(/\n{3,}/g, '\n\n');
  code.forEach((snippet, i) => {
    out = out.split(placeholder(i)).join(snippet);
  });
  // A block moved to its own lines at the very start or end of the text.
  return out.replace(/^\n+|\n+$/g, '') + (text.endsWith('\n') ? '\n' : '');
}
